import { useEffect, useState } from "react";
import { getOpsOverview, OpsTenantRow } from "../lib/api";

/** 📈 營運透視（2026-09-08）：頂層視角——每租戶經營數據（對話/趨勢/客人/轉真人）
 *  ＋LLM 成本／毛利，判斷營商情況、識別 upsell 對象。 */
export default function OpsPage() {
  const [rows, setRows] = useState<OpsTenantRow[]>([]);
  const [totals, setTotals] = useState<ReturnType<typeof Object> | null>(null as any);
  const [sortBy, setSortBy] = useState<"msgs" | "margin" | "cost" | "active">("msgs");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const d = await getOpsOverview();
        setRows(d.tenants || []);
        setTotals(d.totals || null);
      } catch (e: any) {
        setError(e.message || "載入失敗");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sorted = [...rows].sort((a, b) => {
    if (sortBy === "margin") return b.margin_hkd - a.margin_hkd;
    if (sortBy === "cost") return b.est_cost_hkd - a.est_cost_hkd;
    if (sortBy === "active") return b.last_active - a.last_active;
    return b.msgs30d - a.msgs30d;
  });

  const trendCell = (t: OpsTenantRow) => {
    if (t.trend === "new") return <span className="text-emerald-400 font-bold">NEW</span>;
    if (t.trend === "idle" || t.trend === "flat") return <span className="text-slate-500">–</span>;
    const pct = t.trend_pct ?? 0;
    const sign = t.trend === "up" ? "↑" : "↓";
    const cls = t.trend === "up" ? "text-emerald-400" : "text-rose-400";
    return (
      <span className={`${cls} font-bold`}>
        {sign} {Math.abs(pct)}%
      </span>
    );
  };

  const fmtActive = (ms: number) => {
    if (!ms) return "30 日無活動";
    const days = Math.floor((Date.now() - ms) / 86400000);
    if (days === 0) return "今日";
    return `${days} 日前`;
  };

  const topHandoff = [...rows]
    .filter((r) => r.user_msgs30d >= 5)
    .sort((a, b) => b.handoffs30d / Math.max(1, b.user_msgs30d) - a.handoffs30d / Math.max(1, a.user_msgs30d))[0];

  const topCost = [...rows].sort((a, b) => b.est_cost_hkd - a.est_cost_hkd)[0];
  const T: any = totals;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">📈 營運透視</h1>
      <p className="text-slate-400 text-sm mb-6">
        近 30 日窗口——每租戶經營數據＋LLM 實際成本／毛利（帳單級：DeepSeek usage cache hit/miss 拆數＋peak/off-peak；2026-09-18 起生效，之前為估算）。
      </p>

      {loading && <div className="text-slate-400">載入中…</div>}
      {error && <div className="text-rose-400">{error}</div>}

      {!loading && !error && (
        <>
          {/* 頂層卡 */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">30 日總對話</div>
              <div className="text-2xl font-bold">{T?.msgs30d ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">近 7 日 {T?.msgs7d ?? 0}</div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">30 日客人</div>
              <div className="text-2xl font-bold">{T?.guests30d ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">distinct sessions</div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">月費收入（應計）</div>
              <div className="text-2xl font-bold text-emerald-400">HK${T?.fee_hkd ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">trial／平台自用不計</div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">LLM 實際成本（30 日）</div>
              <div className="text-2xl font-bold text-amber-400">HK${T?.cost_hkd ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">DeepSeek V4 費率估算</div>
            </div>
            <div className="bg-slate-900 border border-emerald-700/50 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">毛利（月費−成本）</div>
              <div className={`text-2xl font-bold ${(T?.fee_hkd ?? 0) - (T?.cost_hkd ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                HK${((T?.fee_hkd ?? 0) - (T?.cost_hkd ?? 0)).toFixed(0)}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                增長中 {T?.growing ?? 0}｜萎縮中 {T?.shrinking ?? 0}
              </div>
            </div>
          </div>

          {/* upsell 洞察 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
            {topHandoff && (
              <div className="bg-sky-950/40 border border-sky-800/50 rounded-xl p-4 text-sm">
                <span className="text-sky-300 font-bold">🎯 KB 服務商機：</span>
                <span className="text-slate-300">
                  <b>{topHandoff.bot_name || topHandoff.tenant_id}</b><span className="ml-1 text-xs font-mono text-slate-500">({topHandoff.tenant_id})</span>{" "}
                  轉真人率最高（{topHandoff.handoffs30d} 次／{topHandoff.user_msgs30d} 則）——知識庫可能唔夠用，適合推介師傅重教／KB 服務。
                </span>
              </div>
            )}
            {topCost && topCost.est_cost_hkd > 0 && (
              <div className="bg-amber-950/30 border border-amber-800/50 rounded-xl p-4 text-sm">
                <span className="text-amber-300 font-bold">💰 成本焦點：</span>
                <span className="text-slate-300">
                  <b>{topCost.bot_name || topCost.tenant_id}</b><span className="ml-1 text-xs font-mono text-slate-500">({topCost.tenant_id})</span> 30 日 LLM 成本 HK${topCost.est_cost_hkd}
                  （月費 HK${topCost.fee_hkd}）——{topCost.fee_hkd - topCost.est_cost_hkd < 50 ? "毛利偏薄，留意用量。" : "毛利健康。"}
                </span>
              </div>
            )}
          </div>

          {/* 排序 */}
          <div className="flex items-center gap-3 mb-3 text-sm text-slate-400">
            排序：
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-slate-200"
            >
              <option value="msgs">依對話量</option>
              <option value="margin">依毛利</option>
              <option value="cost">依成本</option>
              <option value="active">依最近活躍</option>
            </select>
          </div>

          {/* 租戶表 */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-800">
                  <th className="p-3">租戶</th>
                  <th className="p-3">方案（月費）</th>
                  <th className="p-3 text-right">30日對話</th>
                  <th className="p-3 text-center">7日趨勢</th>
                  <th className="p-3 text-right">客人</th>
                  <th className="p-3 text-right">AI回覆</th>
                  <th className="p-3 text-right">轉真人</th>
                  <th className="p-3 text-right" title="帳單級：DeepSeek cache hit/miss 拆價＋peak/off-peak">實際成本 HK$</th>
                  <th className="p-3 text-right">毛利 HK$</th>
                  <th className="p-3 text-right">最後活躍</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.tenant_id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                    <td className="p-3">
                      <div className="font-bold text-slate-200">
                        {r.bot_name || r.tenant_id}
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-800 text-[10px] font-mono text-emerald-300 align-middle">{r.tenant_id}</span>
                      </div>
                      <div className="text-xs text-slate-500 font-mono">{r.tenant_id}</div>
                    </td>
                    <td className="p-3 text-slate-300">
                      {r.plan_tier || "—"}
                      <div className="text-xs text-slate-500">HK${r.fee_hkd}</div>
                    </td>
                    <td className="p-3 text-right font-bold">{r.msgs30d}</td>
                    <td className="p-3 text-center">{trendCell(r)}</td>
                    <td className="p-3 text-right text-slate-300">{r.guests30d}</td>
                    <td className="p-3 text-right text-slate-300">{r.ai_replies30d}</td>
                    <td className={`p-3 text-right ${r.handoffs30d > 0 ? "text-sky-300" : "text-slate-500"}`}>{r.handoffs30d}</td>
                    <td className="p-3 text-right text-amber-300">
                      {r.est_cost_hkd || "0"}
                      {typeof r.billed_pct === "number" && r.billed_pct < 100 && (
                        <div className="text-[10px] text-slate-500">帳單覆蓋 {r.billed_pct}%</div>
                      )}
                    </td>
                    <td className={`p-3 text-right font-bold ${r.margin_hkd >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                      {r.margin_hkd}
                    </td>
                    <td className="p-3 text-right text-slate-400 text-xs">{fmtActive(r.last_active)}</td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={10} className="p-6 text-center text-slate-500">
                      暫無租戶數據
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
